/**
 * CockpitHeader -- Sticky non-scrolling header for the Control Center.
 *
 * Shows server badges, connection pulse, health indicators, quick stats,
 * and a toggle for the CapabilitySnapshot card.
 *
 * Positioned sticky at top of the ControlCenterPage.
 */
import { Link } from "react-router-dom";

import { cn } from "../../../utils/cn";
import { Icon } from "../../ui/Icon";
import { Tooltip } from "../../ui/Tooltip";
import { getOverallStats } from "./intentRegistry";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ConnectionStatus = "connected" | "connecting" | "reconnecting" | "error" | "disabled";

export interface CockpitHeaderProps {
  connectionStatus: ConnectionStatus;
  sessionCount: number;
  userCount: number | null;
  deviceCount: number | null;
  embyConfigured: boolean;
  jellyfinConfigured: boolean;
  plexConfigured: boolean;
  isServerDegraded: boolean;
  capabilitySnapshotOpen: boolean;
  onCapabilitySnapshotToggle: () => void;
  /** Opens the cross-server user browse flyout */
  onBrowseUsers?: () => void;
}

// ---------------------------------------------------------------------------
// Status badge config
// ---------------------------------------------------------------------------

const statusConfig: Record<ConnectionStatus, { label: string; className: string; pulseColor: string }> = {
  connected: { label: "Live", className: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30", pulseColor: "bg-emerald-500" },
  connecting: { label: "Connecting", className: "bg-muted/30 text-muted-foreground border-border/40", pulseColor: "bg-muted-foreground" },
  reconnecting: { label: "Reconnecting", className: "bg-amber-500/15 text-amber-600 border-amber-500/30", pulseColor: "bg-amber-500" },
  error: { label: "Error", className: "bg-red-500/15 text-red-600 border-red-500/30", pulseColor: "bg-red-500" },
  disabled: { label: "Disabled", className: "bg-red-500/15 text-red-600 border-red-500/30", pulseColor: "bg-red-500" },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CockpitHeader = ({
  connectionStatus,
  sessionCount,
  userCount,
  deviceCount,
  embyConfigured,
  jellyfinConfigured,
  plexConfigured,
  isServerDegraded,
  capabilitySnapshotOpen,
  onCapabilitySnapshotToggle,
  onBrowseUsers,
}: CockpitHeaderProps) => {
  const status = statusConfig[connectionStatus];
  const stats = getOverallStats();

  return (
    <div className="sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border/50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 mb-6">
      <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Left: Breadcrumb + Status */}
        <div className="flex items-center gap-3 min-w-0">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-label="Breadcrumb">
            <Link
              to="/streaming/dashboard"
              className="hover:text-foreground transition-colors"
            >
              Streaming Dashboard
            </Link>
            <Icon icon="lucide:chevron-right" className="h-3 w-3" aria-hidden />
            <span className="text-foreground font-medium">Control Center</span>
          </nav>

          {/* Connection status badge */}
          <span
            className={cn(
              "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border",
              status.className,
            )}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", status.pulseColor, connectionStatus === "connected" && "animate-pulse")} />
            {status.label}
          </span>

          {/* Degraded indicator */}
          {isServerDegraded && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-600 border border-amber-500/30">
              <Icon icon="lucide:alert-triangle" className="h-3 w-3" aria-hidden />
              Slow
            </span>
          )}
        </div>

        {/* Center: Quick stats */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {/* Server badges */}
          <div className="flex items-center gap-1.5">
            {embyConfigured && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted/30 text-xs">
                <Icon icon="simple-icons:emby" className="h-3 w-3" aria-hidden />
                Emby
              </span>
            )}
            {jellyfinConfigured && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted/30 text-xs">
                <Icon icon="simple-icons:jellyfin" className="h-3 w-3" aria-hidden />
                Jellyfin
              </span>
            )}
            {plexConfigured && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted/30 text-xs">
                <Icon icon="simple-icons:plex" className="h-3 w-3 text-amber-500" aria-hidden />
                Plex
              </span>
            )}
          </div>

          {/* Stat pills */}
          <div className="hidden sm:flex items-center gap-3">
            <StatPill icon="lucide:activity" label="Sessions" value={sessionCount} />
            {userCount !== null && (
              <StatPill icon="lucide:users" label="Users" value={userCount} />
            )}
            {deviceCount !== null && (
              <StatPill icon="lucide:monitor-smartphone" label="Devices" value={deviceCount} />
            )}
            <StatPill icon="lucide:check-circle" label="Live Intents" value={stats.live} />
          </div>
        </div>

        {/* Right: Action buttons */}
        <div className="flex items-center gap-2">
          {/* Browse server users — opens cross-server user flyout */}
          {onBrowseUsers && (
            <Tooltip content="Browse server users" side="bottom">
              <button
                type="button"
                onClick={onBrowseUsers}
                aria-label="Browse server users"
                className={cn(
                  "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors",
                  "border border-border/60",
                  "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70",
                )}
              >
                <Icon icon="lucide:users" className="h-3.5 w-3.5" aria-hidden />
                <span className="hidden sm:inline">Users</span>
              </button>
            </Tooltip>
          )}

          {/* Capability snapshot toggle */}
          <button
            type="button"
            onClick={onCapabilitySnapshotToggle}
            aria-pressed={capabilitySnapshotOpen}
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors",
              "border border-border/60",
              "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70",
              capabilitySnapshotOpen && "bg-muted/40",
            )}
          >
            <Icon icon="lucide:gauge" className="h-3.5 w-3.5" aria-hidden />
            <span className="hidden sm:inline">Capabilities</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// StatPill
// ---------------------------------------------------------------------------

const StatPill = ({ icon, label, value }: { icon: string; label: string; value: number }) => (
  <Tooltip content={label} side="bottom">
    <span className="inline-flex items-center gap-1 text-xs 2xl:text-sm" tabIndex={0}>
      <Icon icon={icon} className="h-3 w-3 2xl:h-3.5 2xl:w-3.5" aria-hidden />
      <span className="font-semibold text-foreground">{value}</span>
      <span className="text-muted-foreground/60">{label}</span>
    </span>
  </Tooltip>
);
